/**
 * filterDnd
 *
 * Shared drag-and-drop protocol for the advanced filter editor. Rail items
 * (properties, variables) and existing filter rows are dragged onto groups / rows
 * using a single typed payload carried on the native `DataTransfer`. Keeping the
 * MIME type + (de)serialization here means the drag sources (rail, rows) and the
 * drop targets (groups, rows) can never disagree on the wire format.
 */
import type React from 'react';

/** Custom MIME so these drags never collide with plain-text drops from elsewhere. */
export const FILTER_DND_MIME = 'application/x-center-filter';

export type FilterDragPayload =
  /**
   * A property dragged from the left rail — dropping adds an unset row for it.
   * `aspectSystemName` mirrors `PropertyOption.aspectSystemName`: present for a
   * composed option, absent for a synthetic one — carried so the drop handler
   * can emit the same two-coordinate `{ aspect, property }` form the click path
   * emits (SC2), never the bare-aspect form.
   */
  | { kind: 'rail-property'; propertyKey: string; aspectSystemName?: string }
  /** A variable (@me, @today…) dragged from the rail — drop on a group to add a
   *  row carrying the token, or on a row to set that row's value. */
  | { kind: 'rail-variable'; token: string }
  /** A lane condition (arc/relationship/perspective) dragged from the rail's
   *  "Relationships" section — dropping adds a lane leaf (a `{property:<laneKey>}`
   *  pseudo-row, same convention as a picked lane type) to the target group
   *  (EAINT-725; perspective added Phase 4b). */
  | { kind: 'rail-lane'; laneKey: 'arc' | 'relationship' | 'perspective' }
  /** An existing leaf row being moved/reordered, addressed by its group path + index. */
  | { kind: 'row'; path: number[]; index: number }
  /**
   * A narrow-host node (leaf or nested group) being dragged onto a group,
   * addressed by id per `filterTreeMove.moveNode` — distinct from `row`,
   * which addresses a leaf by path + index in the advanced editor's
   * `SimpleFilterGroup` tree.
   */
  | { kind: 'group'; nodeId: string };

/** Write a payload onto a drag event (custom MIME + a text/plain fallback). */
export const setFilterDragPayload = (e: React.DragEvent, payload: FilterDragPayload): void => {
  const serialized = JSON.stringify(payload);
  e.dataTransfer.setData(FILTER_DND_MIME, serialized);
  e.dataTransfer.setData('text/plain', serialized);
  e.dataTransfer.effectAllowed = payload.kind === 'row' ? 'move' : 'copy';
};

/** Read a payload from a drop event, or null if it isn't one of ours / is malformed. */
export const readFilterDragPayload = (e: React.DragEvent): FilterDragPayload | null => {
  const raw = e.dataTransfer.getData(FILTER_DND_MIME) || e.dataTransfer.getData('text/plain');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as FilterDragPayload;
    if (
      parsed &&
      (parsed.kind === 'rail-property' ||
        parsed.kind === 'rail-variable' ||
        parsed.kind === 'rail-lane' ||
        parsed.kind === 'row' ||
        parsed.kind === 'group')
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};
